import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Outlet } from "react-router-dom"
import { TabContent, TabPane } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { Button, Flex } from "antd"
import { UserOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import Header from "components/accounting/Header"
import RangeSelector from "components/accounting/RangeSelector"
import Dashboard from "components/accounting/Dashboard"
import Details from "components/accounting/Details"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { CustomSelect } from "ui/Input"

import useSearchFilter from "hooks/useSearchFilter"
import styles from "./Accounting.module.css"
import { useTitle } from "hooks/useTitle"
import { isEqual } from "lodash"

import { ACCESS_ATTRIBUTE, DEFAULT_TYPE, OVERVIEW_RANGE_OPTIONS } from "constants/accounting"
import { getCalendarCrew } from "store/staff"

export default function Accounting() {
  const { t } = useTranslation()
  useTitle(t("accounting.breadcrumb"))
  useLoaderWatchers({ watchers: ["accounting"] })
  const dispatch = useDispatch()

  const RANGE_MENU_OPTIONS = useMemo(
    () => Object.keys(OVERVIEW_RANGE_OPTIONS).map((value) => ({ value, label: t(`accounting.overview_range.${value}`) })),
    [t]
  )
  const [params, onChangeFilters] = useSearchFilter({ accessAttributes: ACCESS_ATTRIBUTE })
  const [paramsState, setParamsState] = useState(params)
  const [salesPage, setSalesPage] = useState(1)
  const [salesPageSize, setSalesPageSize] = useState(10)
  const [expensePage, setExpensePage] = useState(1)
  const [expensePageSize, setExpensePageSize] = useState(10)

  useEffect(() => {
    if (!params.type) onChangeFilters({ type: DEFAULT_TYPE })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setParamsState(params)
  }, [params])

  const changeHandler = useCallback(({ target }) => {
    setParamsState((prev) => ({ ...prev, [target.name]: target.value }))
  }, [])

  const batchChangeHandler = useCallback((batch) => {
    // batch update avoids multiple sequential re-fetches when applying several filters at once
    setParamsState((prev) => ({ ...prev, ...batch }))
    setSalesPage(1)
    setExpensePage(1)
  }, [])

  useEffect(() => {
    if (isEqual(paramsState, params)) return
    onChangeFilters(paramsState)
  }, [paramsState, params, onChangeFilters])

  useEffect(() => { setSalesPage(1) }, [params.date_gteq, params.date_lteq, params.payment_type, params.booking_type, params.transaction_type, params.experience, params.search, params.type]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExpensePage(1) }, [params.date_gteq, params.date_lteq, params.category_id, params.search, params.type]) // eslint-disable-line react-hooks/exhaustive-deps

  const isTipReport = params.type === "tip_report"

  useEffect(() => {
    if (isTipReport) dispatch(getCalendarCrew())
  }, [isTipReport, dispatch])

  const crewProfileItems = useSelector((state) => state.staff.calendarCrew || [])

  const crewOptions = useMemo(
    () => crewProfileItems.map((m) => ({ value: m.id, label: m.name })),
    [crewProfileItems]
  )

  const crewIdsParam = params?.["crew_ids[]"]
  let selectedCrewIds = []
  if (Array.isArray(crewIdsParam)) {
    selectedCrewIds = crewIdsParam
  } else if (crewIdsParam) {
    selectedCrewIds = [crewIdsParam]
  }
  const includeUnassigned = params?.include_unassigned ?? true

  const handleCrewChange = (values) => changeHandler({ target: { name: "crew_ids[]", value: values } })
  const handleIncludeUnassignedToggle = () => changeHandler({ target: { name: "include_unassigned", value: !includeUnassigned } })

  const tipToolbarSlot = isTipReport ? (
    <>
      <CustomSelect
        mode="multiple"
        placeholder={t("accounting.crew_filter.placeholder")}
        value={selectedCrewIds}
        onChange={handleCrewChange}
        options={crewOptions}
        allowClear
        className={styles.crewSelect}
        maxTagCount="responsive"
      />
      <Button
        color="primary"
        variant={includeUnassigned ? "solid" : "outlined"}
        type={includeUnassigned ? "primary" : "default"}
        icon={<UserOutlined />}
        onClick={handleIncludeUnassignedToggle}
      >
        {t("accounting.crew_filter.include_unassigned")}
      </Button>
    </>
  ) : null

  const salesPagination = useMemo(() => ({ page: salesPage, per_page: salesPageSize }), [salesPage, salesPageSize])
  const expensePagination = useMemo(() => ({ page: expensePage, per_page: expensePageSize }), [expensePage, expensePageSize])

  const activeTab = params.type === "business_overview" ? "dashboard" : "details"
  const isBusinessOverview = params.type === "business_overview"

  return (
    <div className="flex-fill z-0 position-relative px-20 py-20" id="accounting">
      <Header params={params} onChangeFilters={changeHandler} salesPagination={salesPagination} expensePagination={expensePagination} />
      <Flex align="center" gap={8} justify="end" wrap className="my-m">
        {isBusinessOverview && (
          <CustomSelect
            value={params.overview_range || "current_month"}
            onChange={(value) => changeHandler({ target: { name: "overview_range", value } })}
            options={RANGE_MENU_OPTIONS}
          />
        )}
        <RangeSelector params={params} onChangeFilters={changeHandler} />
      </Flex>
      <FirstLoading name="accounting">
        <TabContent activeTab={activeTab} className="w-100">
          <TabPane tabId="dashboard">
            <Dashboard params={{ ...params, overview_range: params.overview_range || "current_month" }} onChangeFilters={changeHandler} />
          </TabPane>
          <TabPane tabId="details">
            <Details
              params={params}
              onChangeFilters={changeHandler}
              onChangeFiltersBatch={batchChangeHandler}
              tipToolbarSlot={tipToolbarSlot}
              salesPagination={salesPagination}
              onSalesPageChange={setSalesPage}
              onSalesPageSizeChange={(size) => { setSalesPageSize(size); setSalesPage(1) }}
              expensePagination={expensePagination}
              onExpensePageChange={setExpensePage}
              onExpensePageSizeChange={(size) => { setExpensePageSize(size); setExpensePage(1) }}
            />
          </TabPane>
        </TabContent>

        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </FirstLoading>
    </div>
  )
}
